import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/axios';
import { useAuth } from '../context/AuthContext';
import {
  QUOTATION_LANGUAGES,
  DEFAULT_STATIC_TEXTS_EN,
  DEFAULT_SIGNATURE_EN,
  applyLanguageToQuotationData,
  formatQuotationDate,
  normalizeQuotationLanguage
} from '../utils/quotationLanguages';
import './QuotationBuilder.css';

const DEMO_SIGNATURE = 'Met vriendelijke groet,\n\nSalesmanager Henk de Bont\nSchilderbedrijf Kwastie';

// Standaardteksten voor demo (max 500 karakters per onderdeel); doen alsof AI ze invult
const DEMO_STATIC_TEXTS = {
  greeting: 'Beste heer/mevrouw,\n\nHartelijk dank voor uw interesse. Hierbij ontvangt u ons offertevoorstel voor het aangevraagde schilderwerk. Wij hebben uw aanvraag zorgvuldig bekeken en een passend voorstel voor u uitgewerkt. Graag lichten wij de mogelijkheden en de investering in deze offerte nader toe. Mocht u vragen hebben, neem dan gerust contact met ons op.',
  description: 'Wij hebben de opdracht doorgerekend en bieden u onderstaand een overzicht van de werkzaamheden en de investering. Onze werkzaamheden voeren wij uit met professionele materialen en vakkundige afwerking. Wij staan voor kwaliteit en duurzaamheid. De genoemde prijzen zijn gebaseerd op de door u verstrekte informatie en onze standaard uitvoeringsvoorwaarden. Eventuele meerwerk of wijzigingen kunnen wij separaat voor u begroten.',
  highlight: 'Onze offerte is helder en all-in. Wij zorgen voor nette afwerking en opruiming. Bij vragen staan wij u graag te woord. De genoemde termijnen zijn richtinggevend; bij akkoord stemmen we de planning graag met u af. Wij werken met duurzame materialen en streven naar een resultaat waar u jaren plezier van heeft.',
  closing: 'Heeft u vragen of wilt u deze offerte bespreken? Neem gerust contact met ons op. Wij hopen u binnenkort van dienst te kunnen zijn. U kunt ons bereiken per e-mail of telefoon; wij reageren doorgaans binnen één werkdag. Wij kijken ernaar uit om samen met u dit project tot een goed einde te brengen.'
};

const defaultQuotationData = {
  companyName: '',
  companyTagline: '',
  companyContact: '',
  language: 'nl',
  title: '',
  date: formatQuotationDate('nl'),
  clientName: '',
  reference: '',
  badge: '',
  greeting: '',
  description: '',
  highlight: '',
  closing: '',
  clientData: '',
  pricing: {
    total: 0,
    subtitle: 'excl. btw',
    paymentTerms: '40% / 60%',
    paymentDescription: '40% aanbetaling bij akkoord • 60% bij oplevering',
    showOneTime: true,
    oneTimeAmount: 0,
    oneTimeAmountManual: false,
    showMonthly: false,
    monthlyAmount: 0
  },
  notes: '',
  offerToelichting: ''
};

/** Bouw ondertekeningstekst uit bedrijfsnaam en ondertekenaar (persoon) van het geselecteerde bedrijf */
function buildCompanyContact(companyName, persoon) {
  const lines = ['Met vriendelijke groet,'];
  if (persoon) lines.push('', persoon);
  if (companyName) lines.push(companyName);
  return lines.join('\n').trim();
}

const QuotationBuilder = ({ aiData, selectedProducts, clientName, clientData, selectedCompany, selectedContact, subscriberCompanyName, subscriberPersoon, prefillFromAI, onComplete, onBack, draftQuotationData, draftProducts, standaardinhoudTemplates, existingQuotationId, defaultPaymentSettings }) => {
  const { user } = useAuth();
  const isDemo = user?.isDemo === true;

  const [quotationData, setQuotationData] = useState(() => {
    const base = { ...defaultQuotationData, ...(draftQuotationData || {}), pricing: { ...defaultQuotationData.pricing, ...(draftQuotationData?.pricing || {}) } };
    if (user?.isDemo) {
      base.companyContact = base.companyContact || DEMO_SIGNATURE;
      const tpl = (standaardinhoudTemplates || []).find(t => t.name === 'Algemene voorwaarden schilderwerk');
      if (tpl) {
        base.standaardinhoudTemplate = tpl.name;
        base.standaardinhoud = tpl.content;
      }
    }
    return base;
  });

  // Prijsrange-beschrijvingen (bijv. "€ 1,00 – € 2,00") uit Odoo zijn geen echte beschrijvingen
  const isPriceRange = (d) => d && /^\s*€\s*[\d.,]+\s*[–—\-]\s*€\s*[\d.,]+\s*$/.test(d.trim());
  const isOffertebijlage = (name) => /^Offertebijlage\b/i.test(name);
  const cleanProducts = (prods) => (prods || [])
    .filter(p => !isOffertebijlage(p.name))
    .map(p => ({
      ...p,
      description: isPriceRange(p.description) ? '' : (p.description || '')
    }));

  const [products, setProducts] = useState(
    draftProducts?.length > 0
      ? cleanProducts(draftProducts)
      : selectedProducts && selectedProducts.length > 0
        ? cleanProducts(selectedProducts)
        : [{ name: '', description: '', price: 0, quantity: 1 }]
  );

  // Sync from draft when returning from preview (merge so defaults stay)
  useEffect(() => {
    if (draftQuotationData && Object.keys(draftQuotationData).length > 0) {
      setQuotationData(prev => ({ ...prev, ...draftQuotationData }));
    }
    if (draftProducts && draftProducts.length > 0) {
      setProducts(cleanProducts(draftProducts));
    }
  }, [draftQuotationData, draftProducts]);

  useEffect(() => {
    if (!defaultPaymentSettings) return;
    const { prepayment, delivery } = defaultPaymentSettings;
    const lang = normalizeQuotationLanguage(quotationData.language);
    setQuotationData(prev => {
      const isDefault = prev.pricing.paymentTerms === '40% / 60%' || !prev.pricing.paymentTerms;
      if (!isDefault) return prev;
      const paymentDescription = lang === 'en'
        ? `${prepayment}% deposit upon agreement \u2022 ${delivery}% upon delivery`
        : `${prepayment}% aanbetaling bij akkoord \u2022 ${delivery}% bij oplevering`;
      return {
        ...prev,
        pricing: {
          ...prev.pricing,
          paymentTerms: `${prepayment}% / ${delivery}%`,
          paymentDescription
        }
      };
    });
  }, [defaultPaymentSettings, quotationData.language]);

  // Ondertekening invullen met bedrijfsnaam en ondertekenaar van geselecteerd bedrijf (alleen als veld nog leeg)
  useEffect(() => {
    if (!subscriberCompanyName && !subscriberPersoon) return;
    const signature = buildCompanyContact(subscriberCompanyName || '', subscriberPersoon || '');
    if (!signature.trim()) return;
    setQuotationData(prev => {
      if (prev.companyContact != null && String(prev.companyContact).trim() !== '') return prev;
      return { ...prev, companyContact: signature };
    });
  }, [subscriberCompanyName, subscriberPersoon]);

  // Auto-calculate Odoo-total from products; eenmalig bedrag alleen vullen zolang het niet handmatig is aangepast
  useEffect(() => {
    const total = products.reduce((sum, product) => {
      const price = parseFloat(product.price) || 0;
      const quantity = parseInt(product.quantity) || 0;
      return sum + (price * quantity);
    }, 0);

    const rounded = parseFloat(total.toFixed(2));
    setQuotationData(prev => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        total: rounded,
        ...(prev.pricing.oneTimeAmountManual ? {} : { oneTimeAmount: rounded })
      }
    }));
  }, [products]);

  const [quotationId, setQuotationId] = useState(existingQuotationId || null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchingClient, setSearchingClient] = useState(false);
  const [footerPdfs, setFooterPdfs] = useState([]);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const searchTimeoutRef = useRef(null);

  // Load synced product catalog for "add product" picker
  useEffect(() => {
    api.get('/api/products')
      .then((res) => setCatalogProducts(Array.isArray(res.data) ? res.data : []))
      .catch(() => setCatalogProducts([]));
  }, []);

  useEffect(() => {
    api.get('/api/settings/public').then((res) => {
      const app = res.data?.app || {};
      if (Array.isArray(app.footerPdfs) && app.footerPdfs.length > 0) {
        setFooterPdfs(app.footerPdfs.map(f => ({ name: f.name || 'Footer-PDF' })));
      } else if (app.footerPdfDataUrl) {
        setFooterPdfs([{ name: 'Algemene voorwaarden' }]);
      } else {
        setFooterPdfs([]);
      }
    }).catch(() => setFooterPdfs([]));
  }, []);

  // Alleen product-PDF's tonen; oude offerte-PDF's (Offertebijlage, Voorstel ... S000xx) verbergen
  const isOldOfferPdf = (p) => {
    const name = (p && p.name) ? String(p.name) : '';
    const n = name.toLowerCase();
    return n.includes('offertebijlage') || (n.includes('voorstel') && /\d{4,}|s\d{5}/i.test(name));
  };
  const productPdfAttachments = products.filter(p => p.odoo_attachment_id && !isOldOfferPdf(p));

  // Demo: vaste standaardinhoud (algemene voorwaarden schilderwerk) en ondertekening
  useEffect(() => {
    if (!isDemo || !standaardinhoudTemplates?.length) return;
    const tpl = standaardinhoudTemplates.find(t => t.name === 'Algemene voorwaarden schilderwerk');
    if (tpl) {
      setQuotationData(prev => {
        if (prev.standaardinhoudTemplate === tpl.name) return prev;
        return { ...prev, standaardinhoudTemplate: tpl.name, standaardinhoud: tpl.content, companyContact: prev.companyContact || DEMO_SIGNATURE };
      });
    }
  }, [isDemo, standaardinhoudTemplates]);

  useEffect(() => {
    // Skip prefill if we have draft data (returning from preview)
    if (draftQuotationData && Object.keys(draftQuotationData).length > 0) {
      // Only update clientName and clientData from props
      if (clientName) {
        setQuotationData(prev => ({ ...prev, clientName }));
      }
      if (clientData) {
        setQuotationData(prev => ({ ...prev, clientData }));
      }
      return;
    }

    // Pre-fill from client selection (Step 4)
    if (clientName) {
      setQuotationData(prev => ({
        ...prev,
        clientName: clientName
      }));
    }

    if (clientData) {
      setQuotationData(prev => ({
        ...prev,
        clientData: clientData
      }));
    }

    // Pre-fill ALL fields from AI response (ChatGPT) - EXCEPT clientName which comes from Step 4
    if (prefillFromAI && aiData?.response?.parsedQuotationData) {
      const parsed = aiData.response.parsedQuotationData;
      // Geen generieke placeholder in Aanhef (bijv. "Beste wervende teksten") – laat leeg voor echte placeholder
      const rawGreeting = (parsed.greeting || '').trim();
      const isPlaceholderGreeting = /wervende teksten|Beste heer\/mevrouw/i.test(rawGreeting) || rawGreeting.length < 30;
      const greeting = isPlaceholderGreeting ? '' : (parsed.greeting || '');

      setQuotationData(prev => ({
        ...prev,
        // Keep clientName from props (Step 4), don't override with AI
        title: parsed.title || `Offertevoorstel – ${prev.clientName || 'Project'}`,
        description: parsed.description || '',
        greeting,
        reference: parsed.reference || `REF-${Date.now().toString(36).toUpperCase()}`,
        badge: parsed.badge || '',
        notes: parsed.notes || '',
        pricing: {
          ...prev.pricing,
          total: parsed.pricing?.total || prev.pricing.total,
          subtitle: parsed.pricing?.subtitle || prev.pricing.subtitle,
          paymentTerms: parsed.pricing?.paymentTerms || prev.pricing.paymentTerms,
          paymentDescription: parsed.pricing?.paymentDescription || prev.pricing.paymentDescription,
          showOneTime: parsed.pricing?.showOneTime ?? prev.pricing.showOneTime,
          oneTimeAmount: parsed.pricing?.oneTimeAmount ?? prev.pricing.oneTimeAmount,
          oneTimeAmountManual: parsed.pricing?.oneTimeAmountManual ?? prev.pricing.oneTimeAmountManual,
          showMonthly: parsed.pricing?.showMonthly ?? prev.pricing.showMonthly,
          monthlyAmount: parsed.pricing?.monthlyAmount ?? prev.pricing.monthlyAmount
        }
      }));

      // Set products from AI if available and no products selected
      if (parsed.products && parsed.products.length > 0 && (!selectedProducts || selectedProducts.length === 0)) {
        setProducts(parsed.products);
      }

      // Show success message that fields are pre-filled
      setSuccess('Velden zijn vooringevuld door ChatGPT. Controleer en pas aan indien nodig.');
    } else if (aiData?.response) {
      // Fallback: basic pre-fill from raw AI response
      setQuotationData(prev => ({
        ...prev,
        title: prev.title || `Offertevoorstel – ${prev.clientName || 'Project'}`,
        greeting: prev.greeting || '',
        reference: prev.reference || `REF-${Date.now().toString(36).toUpperCase()}`
      }));
    } else {
      // No AI data - generate basic structure (only if fields are empty)
      setQuotationData(prev => ({
        ...prev,
        title: prev.title || `Offertevoorstel – ${prev.clientName || 'Project'}`,
        greeting: prev.greeting || '',
        reference: prev.reference || `REF-${Date.now().toString(36).toUpperCase()}`
      }));
    }
  }, [aiData, clientName, clientData, selectedProducts, selectedContact, prefillFromAI, draftQuotationData]);

  const searchClientInOdoo = async (clientName) => {
    if (!clientName || clientName.trim() === '') {
      return;
    }

    setSearchingClient(true);
    try {
      const response = await api.post('/api/odoo/search-client', {
        clientName: clientName.trim()
      });

      if (response.data.found && response.data.clientData) {
        setQuotationData(prev => ({
          ...prev,
          clientData: response.data.clientData.clientData,
          clientName: response.data.clientData.name
        }));
        setSuccess(`Klantgegevens opgehaald uit Odoo: ${response.data.clientData.name}`);
      }
    } catch (err) {
      // Silently fail - client might not exist in Odoo
      console.log('Client not found in Odoo or Odoo not configured');
    } finally {
      setSearchingClient(false);
    }
  };

  const handleClientNameChange = (value) => {
    handleInputChange('clientName', value);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce search in Odoo
    if (value && value.length > 2) {
      searchTimeoutRef.current = setTimeout(() => {
        searchClientInOdoo(value);
      }, 1000);
    }
  };

  const handleInputChange = (field, value) => {
    setQuotationData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLanguageChange = (newLanguage) => {
    setQuotationData(prev => applyLanguageToQuotationData(prev, newLanguage));
  };

  const handlePricingChange = (field, value) => {
    setQuotationData(prev => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        ...(field === 'oneTimeAmount' ? { oneTimeAmountManual: true } : {}),
        [field]: ['total', 'oneTimeAmount', 'monthlyAmount'].includes(field)
          ? parseFloat(value) || 0
          : field.startsWith('show')
            ? !!value
            : value
      }
    }));
  };

  const handleProductChange = (index, field, value) => {
    const updatedProducts = [...products];
    updatedProducts[index] = {
      ...updatedProducts[index],
      [field]: field === 'price' || field === 'quantity' ? parseFloat(value) || 0 : value
    };
    setProducts(updatedProducts);

    // Auto-calculate total
    const total = updatedProducts.reduce((sum, p) => sum + (p.price * (p.quantity || 1)), 0);
    handlePricingChange('total', total);
  };

  const addProduct = () => {
    setProducts([...products, { name: '', description: '', price: 0, quantity: 1 }]);
  };

  const addCatalogProduct = (catalogProd) => {
    const isPriceRangeDesc = isPriceRange(catalogProd.description);
    setProducts([...products, {
      id: catalogProd.id,
      name: catalogProd.name || '',
      description: isPriceRangeDesc ? '' : (catalogProd.description || ''),
      price: parseFloat(catalogProd.price) || 0,
      quantity: 1
    }]);
    setShowProductPicker(false);
    setProductSearch('');
  };

  const removeProduct = (index) => {
    setProducts(products.filter((_, i) => i !== index));
  };

  const generateTextsWithAI = async () => {
    setGeneratingAI(true);
    setAiError(null);

    if (isDemo) {
      // Doe alsof AI schrijft: 1 sec wachten, daarna standaardteksten invullen (max 500 tekens per onderdeel)
      await new Promise(r => setTimeout(r, 1000));
      const lang = normalizeQuotationLanguage(quotationData.language);
      const demoTexts = lang === 'en' ? DEFAULT_STATIC_TEXTS_EN : DEMO_STATIC_TEXTS;
      setQuotationData(prev => ({
        ...prev,
        greeting: (demoTexts.greeting || '').slice(0, 500),
        description: (demoTexts.description || '').slice(0, 500),
        highlight: (demoTexts.highlight || '').slice(0, 500),
        closing: (demoTexts.closing || '').slice(0, 500),
        companyContact: prev.companyContact || (lang === 'en' ? DEFAULT_SIGNATURE_EN : DEMO_SIGNATURE)
      }));
      setSuccess('Standaardteksten ingevuld. Pas aan waar nodig.');
      setTimeout(() => setSuccess(null), 4000);
      setGeneratingAI(false);
      return;
    }

    try {
      const response = await api.post('/api/openai/generate-quotation-texts', {
        title: quotationData.title || 'Offerte',
        badge: quotationData.badge || '',
        greeting: quotationData.greeting || '',
        description: quotationData.description || '',
        highlight: quotationData.highlight || '',
        closing: quotationData.closing || '',
        clientName: quotationData.clientName || clientName || '',
        contactName: selectedContact?.name || '',
        products: products.filter(p => p.name).map(p => ({
          name: p.name,
          description: p.description || '',
          price: p.price || 0
        })),
        aiInfoInput: aiData?.query || '',
        aiInfoResponse: aiData?.response || null,
        language: quotationData.language || 'nl'
      });

      if (response.data.success && response.data.texts) {
        setQuotationData(prev => ({
          ...prev,
          title: response.data.texts.title || prev.title,
          badge: response.data.texts.badge || prev.badge,
          greeting: response.data.texts.greeting || prev.greeting,
          description: response.data.texts.description || prev.description,
          highlight: response.data.texts.highlight || prev.highlight,
          closing: response.data.texts.closing || prev.closing
        }));
        setSuccess('✅ Teksten gegenereerd met AI - bekijk en pas aan waar nodig!');
        setTimeout(() => setSuccess(null), 5000);
      }
    } catch (err) {
      console.error('AI generation error:', err);
      const errorMsg = err.response?.data?.error || err.response?.data?.message || 'Fout bij het genereren van teksten met AI';
      setAiError(errorMsg);
    } finally {
      setGeneratingAI(false);
    }
  };

  const saveQuotation = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Bewaar ALLE producten met naam (ook zonder product-ID)
      const allNamedProducts = products.filter(p => p.name);
      const productIds = allNamedProducts.map(p => p.id || null);

      const productsPayload = allNamedProducts.map(p => ({
        name: p.name || '',
        quantity: p.quantity ?? 1,
        price: parseFloat(p.price) || 0,
        description: p.description || ''
      }));

      const quotationPayload = {
        quotationData: {
          title: quotationData.title,
          client_name: quotationData.clientName,
          client_data: quotationData.clientData,
          description: quotationData.description,
          greeting: quotationData.greeting,
          reference: quotationData.reference,
          badge: quotationData.badge,
          total_price: quotationData.pricing.total,
          highlight: quotationData.highlight,
          closing: quotationData.closing,
          company_contact: quotationData.companyContact,
          valid_until: quotationData.validUntil,
          payment_terms: quotationData.pricing?.paymentTerms,
          payment_description: quotationData.pricing?.paymentDescription,
          price_subtitle: quotationData.pricing?.subtitle,
          language: quotationData.language || 'nl',
          show_one_time: quotationData.pricing?.showOneTime,
          one_time_amount: quotationData.pricing?.oneTimeAmount,
          one_time_amount_manual: quotationData.pricing?.oneTimeAmountManual ?? false,
          show_monthly: quotationData.pricing?.showMonthly,
          monthly_amount: quotationData.pricing?.monthlyAmount,
          standaardinhoud: quotationData.standaardinhoud || '',
          standaardinhoud_template: quotationData.standaardinhoudTemplate || '',
          offer_toelichting: quotationData.offerToelichting || '',
          ai_query: quotationData.aiQuery || '',
          products: productsPayload
        },
        productIds
      };

      let response;
      if (quotationId) {
        // Update existing quotation (inclusief producten)
        response = await api.put(`/api/quotations/${quotationId}`, {
          ...quotationPayload.quotationData,
          products: productsPayload,
          productIds
        });
      } else {
        // Create new quotation
        response = await api.post('/api/quotations', quotationPayload);
        setQuotationId(response.data.id);
      }

      setSuccess('Offerte opgeslagen!');
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save quotation');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // First save the quotation if not saved yet
      let currentQuotationId = quotationId;
      if (!currentQuotationId) {
        const saved = await saveQuotation();
        currentQuotationId = saved.id;
      }

      // Generate PDF (meesturen huidige ondertekening en teksten zodat PDF overeenkomt met scherm)
      const response = await api.post(
        `/api/quotations/${currentQuotationId}/generate-pdf`,
        {
          companyContact: quotationData.companyContact ?? '',
          highlight: quotationData.highlight,
          closing: quotationData.closing,
          validUntil: quotationData.validUntil,
          standaardinhoud: quotationData.standaardinhoud,
          offer_toelichting: quotationData.offerToelichting,
          price_subtitle: quotationData.pricing?.subtitle,
          language: quotationData.language || 'nl',
          payment_terms: quotationData.pricing?.paymentTerms,
          payment_description: quotationData.pricing?.paymentDescription,
          show_one_time: quotationData.pricing?.showOneTime,
          one_time_amount: quotationData.pricing?.oneTimeAmount,
          one_time_amount_manual: quotationData.pricing?.oneTimeAmountManual ?? false,
          show_monthly: quotationData.pricing?.showMonthly,
          monthly_amount: quotationData.pricing?.monthlyAmount
        },
        { responseType: 'blob' }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `offerte-${quotationData.reference || currentQuotationId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      setSuccess('PDF succesvol gegenereerd en gedownload!');
      
      if (onComplete) {
        onComplete({ quotationId: currentQuotationId, step: 'pdf_generated' });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate PDF');
    } finally {
      setLoading(false);
    }
  };

  const syncToOdoo = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Ensure quotation is saved
      let currentQuotationId = quotationId;
      if (!currentQuotationId) {
        const saved = await saveQuotation();
        currentQuotationId = saved.id;
      }

      // Sync to Odoo
      const response = await api.post(`/api/quotations/${currentQuotationId}/sync-odoo`);

      setSuccess(`Succesvol gesynchroniseerd naar Odoo! Verkoopkans ID: ${response.data.opportunityId}, Offerte ID: ${response.data.saleOrderId}`);
      
      if (onComplete) {
        onComplete({ quotationId: currentQuotationId, step: 'synced_odoo', odooData: response.data });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to sync to Odoo');
    } finally {
      setLoading(false);
    }
  };

  const uploadPDFToOdoo = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (!quotationId) {
        setError('Offerte moet eerst worden opgeslagen en PDF gegenereerd');
        return;
      }

      const response = await api.post(`/api/quotations/${quotationId}/upload-pdf-odoo`);

      setSuccess('PDF succesvol geüpload naar Odoo!');
      
      if (onComplete) {
        onComplete({ quotationId, step: 'pdf_uploaded' });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload PDF to Odoo');
    } finally {
      setLoading(false);
    }
  };

  const requestRevision = async (instructions) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (!quotationId) {
        setError('Offerte moet eerst worden opgeslagen');
        return;
      }

      const response = await api.post(`/api/quotations/${quotationId}/revisions`, {
        instructions
      });

      setSuccess(`Revisie aangevraagd (revisie #${response.data.revision_number})`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to request revision');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="quotation-builder">
      <div className="container">
        <div className="header">
          <h1 className="logo">OfferteRunner</h1>
          <p className="tagline">Offerte Builder</p>
        </div>

        {error && <div className="error">{error}</div>}
        {success && <div className="success">{success}</div>}

        <div className="action-buttons" style={{ marginBottom: 24 }} data-demo-step="demo-step-4">
          <button
            className="btn btn-secondary"
            onClick={() => onComplete && onComplete({ step: 'preview', quotationData, products, quotationId })}
          >
            👁 Bekijk voorbeeld
          </button>
          <button
            className="btn btn-secondary"
            onClick={saveQuotation}
            disabled={loading}
          >
            {loading ? 'Opslaan...' : quotationId ? '💾 Offerte bijwerken' : '💾 Opslaan'}
          </button>
        </div>

        <div className="card">
          <h2>Basisinformatie</h2>

          <div className="input-group" style={{ marginBottom: 16 }}>
            <label>Taal offerte</label>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {QUOTATION_LANGUAGES.map(({ code, label, flag }) => (
                <button
                  key={code}
                  type="button"
                  className={`btn ${normalizeQuotationLanguage(quotationData.language) === code ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => handleLanguageChange(code)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                >
                  <span aria-hidden="true">{flag}</span>
                  {label}
                </button>
              ))}
            </div>
            <p style={{ fontSize: 12, color: '#6b7280', margin: '8px 0 0' }}>
              De bediening blijft Nederlands. Gekozen taal geldt voor de offerte-PDF, labels en AI-teksten.
            </p>
          </div>
          
          <div className="form-grid">
            <div className="input-group">
              <label>Titel</label>
              <input
                type="text"
                value={quotationData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder={normalizeQuotationLanguage(quotationData.language) === 'en' ? 'Quotation proposal – Project name' : 'Offertevoorstel – Projectnaam'}
              />
            </div>

            <div className="input-group">
              <label>Datum</label>
              <input
                type="text"
                value={quotationData.date}
                onChange={(e) => handleInputChange('date', e.target.value)}
              />
            </div>

            <div className="input-group">
              <label>
                Opdrachtgever
                {searchingClient && <span style={{ marginLeft: '8px', fontSize: '12px', color: '#6b7280' }}>Zoeken in Odoo...</span>}
              </label>
              <input
                type="text"
                value={quotationData.clientName}
                onChange={(e) => handleClientNameChange(e.target.value)}
                placeholder="Naam van de klant"
              />
              {quotationData.clientName && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => searchClientInOdoo(quotationData.clientName)}
                  style={{ marginTop: '8px', fontSize: '12px', padding: '6px 12px' }}
                  disabled={searchingClient}
                >
                  🔍 Zoek in Odoo
                </button>
              )}
            </div>

            <div className="input-group">
              <label>Referentie</label>
              <input
                type="text"
                value={quotationData.reference}
                onChange={(e) => handleInputChange('reference', e.target.value)}
                placeholder="Project referentie"
              />
            </div>

            <div className="input-group">
              <label>Badge</label>
              <input
                type="text"
                value={quotationData.badge}
                onChange={(e) => handleInputChange('badge', e.target.value)}
                placeholder="Bijv: Conversiegericht Webredesign + SEO"
              />
            </div>
          </div>
        </div>

        <div className="card">
          <h2>Producten & Diensten</h2>
          
          {products.map((product, index) => (
            <div key={index} className="product-row">
              <div className="input-group">
                <label>Productnaam</label>
                <input
                  type="text"
                  value={product.name}
                  onChange={(e) => handleProductChange(index, 'name', e.target.value)}
                  placeholder="Naam van het product"
                />
              </div>

              <div className="input-group">
                <label>Beschrijving</label>
                <textarea
                  value={product.description}
                  onChange={(e) => handleProductChange(index, 'description', e.target.value)}
                  placeholder="Productbeschrijving"
                  rows={2}
                />
              </div>

              <div className="input-group">
                <label>Prijs (€)</label>
                <input
                  type="number"
                  step="0.01"
                  value={product.price}
                  onChange={(e) => handleProductChange(index, 'price', e.target.value)}
                  placeholder="0.00"
                />
              </div>

              <div className="input-group">
                <label>Aantal</label>
                <input
                  type="number"
                  value={product.quantity}
                  onChange={(e) => handleProductChange(index, 'quantity', e.target.value)}
                  placeholder="1"
                />
              </div>

              {products.length > 1 && (
                <button
                  type="button"
                  className="btn btn-secondary remove-btn"
                  onClick={() => removeProduct(index)}
                >
                  Verwijder
                </button>
              )}
            </div>
          ))}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {catalogProducts.length > 0 && (
              <button type="button" className="btn btn-primary btn-small" onClick={() => setShowProductPicker(!showProductPicker)}>
                + Product uit catalogus
              </button>
            )}
            <button type="button" className="btn btn-secondary btn-small" onClick={addProduct}>
              + Vrij product
            </button>
          </div>

          {showProductPicker && (
            <div style={{ marginTop: 12, border: '1px solid #d1d5db', borderRadius: 8, padding: 12, background: '#f9fafb', maxHeight: 300, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <input
                type="text"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder="Zoek product..."
                style={{ marginBottom: 8, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
                autoFocus
              />
              <div style={{ overflowY: 'auto', maxHeight: 220 }}>
                {catalogProducts
                  .filter(p => !productSearch || p.name?.toLowerCase().includes(productSearch.toLowerCase()))
                  .map((p) => (
                    <div
                      key={p.id}
                      onClick={() => addCatalogProduct(p)}
                      style={{ padding: '8px 10px', cursor: 'pointer', borderBottom: '1px solid #e5e7eb', fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#eef2ff'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <span>{p.name}</span>
                      <span style={{ color: '#6b7280', fontSize: 12, marginLeft: 12, whiteSpace: 'nowrap' }}>
                        {p.price != null ? `€ ${parseFloat(p.price).toFixed(2)}` : ''}
                      </span>
                    </div>
                  ))}
                {catalogProducts.filter(p => !productSearch || p.name?.toLowerCase().includes(productSearch.toLowerCase())).length === 0 && (
                  <div style={{ padding: 10, color: '#6b7280', fontSize: 13 }}>Geen producten gevonden</div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="card">
          <h2>Klantgegevens</h2>
          <div className="input-group">
            <label>Klantgegevens (volledig adres)</label>
            <textarea
              value={quotationData.clientData}
              onChange={(e) => handleInputChange('clientData', e.target.value)}
              placeholder="Bedrijfsnaam&#10;t.a.v. Naam&#10;Adres&#10;Tel: ...&#10;E-mail: ...&#10;KvK: ... | BTW: ..."
              rows={6}
            />
          </div>
        </div>

        <div className="card">
          <h2>Investering</h2>
          <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 12px' }}>
            Kies welke tariefregels op de offerte verschijnen. Het producttotaal (€ {quotationData.pricing.total.toFixed(2).replace('.', ',')}) wordt gebruikt voor Odoo-sync.
          </p>
          <div className="form-grid">
            <div className="input-group" style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!quotationData.pricing.showOneTime}
                  onChange={(e) => handlePricingChange('showOneTime', e.target.checked)}
                />
                Eenmalig tonen op offerte
              </label>
              {quotationData.pricing.showOneTime && (
                <input
                  type="number"
                  step="0.01"
                  value={quotationData.pricing.oneTimeAmount}
                  onChange={(e) => handlePricingChange('oneTimeAmount', e.target.value)}
                  placeholder="Eenmalig bedrag (€)"
                  style={{ marginTop: 8 }}
                />
              )}
            </div>

            <div className="input-group" style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!quotationData.pricing.showMonthly}
                  onChange={(e) => handlePricingChange('showMonthly', e.target.checked)}
                />
                Maandelijks tonen op offerte
              </label>
              {quotationData.pricing.showMonthly && (
                <input
                  type="number"
                  step="0.01"
                  value={quotationData.pricing.monthlyAmount}
                  onChange={(e) => handlePricingChange('monthlyAmount', e.target.value)}
                  placeholder="Maandelijks bedrag (€)"
                  style={{ marginTop: 8 }}
                />
              )}
            </div>

            <div className="input-group">
              <label>Ondertitel (bij tariefregels)</label>
              <input
                type="text"
                value={quotationData.pricing.subtitle}
                onChange={(e) => handlePricingChange('subtitle', e.target.value)}
                placeholder={normalizeQuotationLanguage(quotationData.language) === 'en' ? 'excl. VAT • description' : 'excl. btw • beschrijving'}
              />
            </div>

            <div className="input-group">
              <label>Betalingsvoorwaarden (bijv: 40% / 60%)</label>
              <input
                type="text"
                value={quotationData.pricing.paymentTerms}
                onChange={(e) => handlePricingChange('paymentTerms', e.target.value)}
                placeholder="40% / 60%"
              />
            </div>

            <div className="input-group">
              <label>Betalingsbeschrijving</label>
              <input
                type="text"
                value={quotationData.pricing.paymentDescription}
                onChange={(e) => handlePricingChange('paymentDescription', e.target.value)}
                placeholder={normalizeQuotationLanguage(quotationData.language) === 'en' ? '40% deposit • 60% upon delivery' : '40% aanbetaling • 60% bij oplevering'}
              />
            </div>
          </div>
        </div>

        <div className="card">
          <h2>Standaardinhoud & toelichting</h2>
          {Array.isArray(standaardinhoudTemplates) && standaardinhoudTemplates.length > 0 && (
            <>
              <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 12px' }}>
                Kies een template met vaste tekst die tussen Investering en Afsluiting in de offerte komt.
              </p>
              <div className="input-group">
                <label>Template</label>
                <select
                  value={quotationData.standaardinhoudTemplate || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '') {
                      handleInputChange('standaardinhoudTemplate', '');
                      handleInputChange('standaardinhoud', '');
                    } else {
                      const tpl = standaardinhoudTemplates.find(t => t.name === val);
                      handleInputChange('standaardinhoudTemplate', val);
                      handleInputChange('standaardinhoud', tpl ? tpl.content : '');
                    }
                  }}
                >
                  <option value="">— Geen standaardinhoud —</option>
                  {standaardinhoudTemplates.map((tpl, i) => (
                    <option key={i} value={tpl.name}>{tpl.name}</option>
                  ))}
                </select>
              </div>
              {quotationData.standaardinhoud && (
                <div style={{ marginTop: 8, padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#fafafa', fontSize: 12, maxHeight: 200, overflow: 'auto' }}>
                  <div dangerouslySetInnerHTML={{ __html: quotationData.standaardinhoud }} />
                </div>
              )}
            </>
          )}

          <div className="input-group" style={{ marginTop: Array.isArray(standaardinhoudTemplates) && standaardinhoudTemplates.length > 0 ? 16 : 0 }}>
            <label>Offertespecifieke toelichting (HTML)</label>
            <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 8px' }}>
              Optioneel extra blok onder de standaardinhoud, alleen voor deze offerte. HTML is toegestaan.
            </p>
            <textarea
              value={quotationData.offerToelichting || ''}
              onChange={(e) => handleInputChange('offerToelichting', e.target.value)}
              placeholder={'<p>Bijv. aanvullende voorwaarden of toelichting specifiek voor deze offerte.</p>'}
              rows={8}
              style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace', fontSize: 13 }}
            />
          </div>
          {quotationData.offerToelichting && (
            <div style={{ marginTop: 8, padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#fafafa', fontSize: 12, maxHeight: 240, overflow: 'auto' }}>
              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6 }}>Voorbeeld:</div>
              <div dangerouslySetInnerHTML={{ __html: quotationData.offerToelichting }} />
            </div>
          )}
        </div>

        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ margin: 0 }}>Aanhef & Beschrijving</h2>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={generateTextsWithAI}
              disabled={generatingAI}
              style={{ fontSize: 14, padding: '8px 16px' }}
            >
              {generatingAI ? 'Bezig...' : '🤖 Genereer teksten met AI'}
            </button>
          </div>

          {aiError && (
            <div className="error" style={{ marginBottom: 12 }}>{aiError}</div>
          )}

          <div className="input-group">
            <label>Aanhef</label>
            <textarea
              value={quotationData.greeting}
              onChange={(e) => handleInputChange('greeting', e.target.value)}
              placeholder={isDemo ? "Bijv. Beste heer/mevrouw,\n\nHartelijk dank voor uw interesse." : "Geachte Hans Bakker,\n\nHartelijk dank voor uw interesse in onze WPMUDEV Superplugins. Wij zijn enthousiast om u te ondersteunen bij het verbeteren van uw online prestaties. Onze Superplugins zijn ontworpen om uw website naar een hoger niveau te tillen, zodat u optimaal kunt profiteren van de mogelijkheden die het internet biedt."}
              rows={6}
            />
          </div>

          <div className="input-group">
            <label>Beschrijving</label>
            <textarea
              value={quotationData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder={"In deze offerte presenteren wij een unieke kans voor uw onderneming. Wij bieden een investering van €1 aan, die als basis dient voor een uitgebreide samenwerking. Onze focus ligt op het creëren van waarde door strategische inzichten en slimme oplossingen te bieden. We begrijpen dat elke onderneming uniek is en daarom stemmen we onze aanpak af op uw specifieke behoeften en doelen. Met onze investering krijgt u toegang tot een scala aan mogelijkheden die u in staat stellen om uw processen te optimaliseren en uw rendement te verhogen. Wij combineren diepgaande kennis van de markt met innovatieve strategieën om ervoor te zorgen dat u de beste resultaten behaalt. Dit voorstel vormt de eerste stap naar een succesvolle samenwerking die niet alleen financieel voordelig is, maar ook bijdraagt aan de lange termijn groei van uw organisatie."}
              rows={8}
            />
          </div>

          <div className="input-group">
            <label>Kern van het aanbod (highlight)</label>
            <textarea
              value={quotationData.highlight}
              onChange={(e) => handleInputChange('highlight', e.target.value)}
              placeholder={"Onze investering biedt u niet alleen financiële voordelen, maar ook strategische begeleiding die uw onderneming duurzaam zal versterken. Wij onderscheiden ons door onze klantgerichte aanpak en onze focus op meetbare resultaten. Met ons aan uw zijde kunt u rekenen op deskundigheid en ondersteuning die u helpen om succesvol te zijn in de dynamische markt van vandaag."}
              rows={4}
            />
          </div>

          <div className="input-group">
            <label>Afsluiting</label>
            <textarea
              value={quotationData.closing}
              onChange={(e) => handleInputChange('closing', e.target.value)}
              placeholder={"Wij zijn ervan overtuigd dat deze samenwerking een waardevolle aanvulling zal zijn voor uw onderneming. Laten we samen de mogelijkheden verkennen en de eerste stappen zetten naar een succesvolle toekomst. Wij nodigen u uit om met ons in gesprek te gaan, zodat we uw vragen kunnen beantwoorden en de details van deze samenwerking kunnen bespreken. Neem gerust contact met ons op via e-mail of telefoon. Wij kijken ernaar uit om u te ondersteunen in uw groeidoelstellingen en zijn enthousiast om deze kans samen met u te benutten. Laten we de toekomst samen vormgeven!"}
              rows={6}
            />
          </div>

          <div className="input-group" data-demo-step="ondertekening">
            <label>Ondertekening / Afronding offerte</label>
            <textarea
              value={quotationData.companyContact}
              onChange={(e) => handleInputChange('companyContact', e.target.value)}
              placeholder="Bijv.: Met vriendelijke groet,&#10;&#10;Salesmanager [Naam]&#10;[Bedrijfsnaam]"
              rows={4}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotationBuilder;
